import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import {
  AbstractControl,
  FormControlStatus,
  FormGroup,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { moveItemInArray } from '@angular/cdk/drag-drop';
import { takeUntil } from 'rxjs';
import { tuiPure, TuiStringHandler, TuiContext } from '@taiga-ui/cdk';
import { isEqual } from 'lodash-es';
import { TranslateService } from '@ngx-translate/core';
import { BaseResizableComponent } from '@src/app/modules/base';
import { FIELD_TYPES, FieldType, FieldTypes } from '@src/app/shared/field';
import { APP_CONFIG } from '@src/core';
import {
  isFileContact,
  isListContact,
  isNotOtherAndNotFileAndNotListContact,
  isOtherContact,
  removeContactsByUserPermissions,
} from '@src/utils';
import { UserService } from '@src/core/services';
import { ContactUI } from '@src/models';
import { Nullable, Optional } from '@src/types/utils';
import { EnvService } from '@src/app/modules/env';
import { CustomListService } from '@src/app/modules/contacts-directory/services';
import { ContactsCustomListItem } from '@src/app/modules/contacts-directory';
import { atLeastOneValidator } from '@src/app/validators';

@Component({
  selector: 'contacts-view-old',
  templateUrl: './contacts-view-old.component.html',
  styleUrls: ['./contacts-view-old.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class ContactsViewOldComponent extends BaseResizableComponent implements OnChanges {
  private readonly translateService = inject(TranslateService);
  private readonly formBuilder = inject(UntypedFormBuilder);
  private readonly userService = inject(UserService);
  private readonly env = inject(EnvService);
  private readonly customListService = inject(CustomListService);

  @Input() set value(contacts: Array<ContactUI>) {
    if (!isEqual(contacts, this.contacts.value)) {
      this.contacts.clear();
      contacts.forEach(() => {
        this.addContact();
      });

      this.form.patchValue({ contacts });
    }
    this.cdr.markForCheck();
  }
  @Input() allowCreating: Optional<Nullable<boolean>> = false;
  @Input() allowEditing: Optional<Nullable<boolean>> = false;
  @Input() allowDeleting: Optional<Nullable<boolean>> = false;
  @Input() allowFilesDeleting: Optional<Nullable<boolean>> = true;
  @Input() loading = false;
  @Input() disabled = false;
  @Input() isModeration: Optional<Nullable<boolean>> = false;
  @Input() showFieldCounter: Optional<Nullable<boolean>> = true;
  @Input() showFieldSeparator: Optional<Nullable<boolean>> = true;
  @Input() showFieldValue: boolean = true;
  @Input() touched: Optional<Nullable<boolean>> = false;

  @Input() acceptFiles: string = '';
  @Input() contactMaxLength: number = 2000;
  @Input() descriptionMaxLength: number = 2000;

  @Output() valueChange = new EventEmitter<Array<ContactUI>>();
  @Output() statusChange = new EventEmitter<FormControlStatus>();

  customListDirectories$ = this.customListService.customListDirectories$;

  form!: UntypedFormGroup;
  contactTypes: FieldType[] = Object.values(FIELD_TYPES).filter(fieldType => fieldType.forContacts);
  readonly maxDocFileSize = APP_CONFIG.fileSizeMax.doc;

  isFileContactLocal = isFileContact;
  isOtherContactLocal = isOtherContact;
  isNotOtherAndNotFileAndNotListContactLocal = isNotOtherAndNotFileAndNotListContact;
  isListContactLocal = isListContact;

  private readonly fileValidatorByForm = atLeastOneValidator('newDocument', 'oldDocument');

  constructor() {
    super();

    this.initForm();
  }

  override ngOnInit(): void {
    super.ngOnInit();

    this.contactTypes = removeContactsByUserPermissions(this.contactTypes, this.userService.authUser?.permissions);

    this.contacts.valueChanges.pipe(takeUntil(this.destroyed$$)).subscribe(value => {
      this.valueChange.emit(value);
    });

    this.contacts.statusChanges.pipe(takeUntil(this.destroyed$$)).subscribe(value => {
      this.statusChange.emit(value);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.touched?.currentValue === true) {
      this.contacts?.markAllAsTouched();
    }

    if (changes.disabled) {
      if (this.disabled) {
        this.contacts.controls.forEach(control => {
          control.disable();
        });
      } else {
        this.contacts.controls.forEach(control => {
          control.enable();
        });
      }
    }

    if (changes.allowEditing && this.allowEditing) {
      this.customListService.getCustomListDirectories();
    }
  }

  get contacts(): UntypedFormArray {
    return this.form.get('contacts') as UntypedFormArray;
  }

  getDocumentFormControl(contact: AbstractControl, document: string): UntypedFormControl {
    return contact.get(document) as UntypedFormControl;
  }

  resetDocumentFormControl(contact: AbstractControl, document: string) {
    (contact.get(document) as UntypedFormControl).reset();
    (contact.get('contact') as UntypedFormControl).reset();
  }

  createContact(): UntypedFormGroup {
    const newSortOrder = (this.contacts.length + 1) * 10;
    return this.formBuilder.group({
      id: [undefined],
      contactTypeId: [1],
      customListId: [null],
      customListValueId: [null],
      contact: ['', Validators.maxLength(this.contactMaxLength)],
      oldDocument: [''],
      newDocument: [''],
      description: ['', Validators.maxLength(this.descriptionMaxLength)],
      sortOrder: [newSortOrder],
      isPriority: [false],
      isRequired: [false],
      contactListValues: [null],
    });
  }

  removeContact(index: number): void {
    this.contacts.removeAt(index);
  }

  moveContact(currentIndex: number, index: number) {
    const currentSortOrder = this.contacts.controls[currentIndex].value.sortOrder;
    const newSortOrder = this.contacts.controls[currentIndex + index].value.sortOrder;
    this.contacts.controls[currentIndex].patchValue({ sortOrder: newSortOrder });
    this.contacts.controls[currentIndex + index].patchValue({ sortOrder: currentSortOrder });

    moveItemInArray(this.contacts.controls, currentIndex, currentIndex + index);
  }

  addContact(): void {
    const createdContactControl = this.createContact();

    const isRequiredControl = createdContactControl.controls.isRequired;
    const contactTypeIdControl = createdContactControl.controls.contactTypeId;
    const customListIdControl = createdContactControl.controls.customListId;
    const customListValueIdControl = createdContactControl.controls.customListValueId;

    isRequiredControl.valueChanges.subscribe(value => {
      const contactTypeId = contactTypeIdControl.value;

      if (this.isListContactLocal(contactTypeId)) {
        this.switchRequiredValidator(customListValueIdControl, value);
      } else if (this.isFileContactLocal(contactTypeId)) {
        this.switchFileValidatorByForm(createdContactControl, value);
      } else {
        const contactControl = createdContactControl.controls.contact;
        this.switchRequiredValidator(contactControl, value);
      }
    });

    contactTypeIdControl?.valueChanges.pipe(takeUntil(this.destroyed$$)).subscribe(contactTypeId => {
      if (contactTypeIdControl?.dirty) {
        createdContactControl.patchValue(
          { customListId: null, customListValueId: null, contact: null },
          { emitEvent: false },
        );
      }

      if (contactTypeId === FieldTypes.List && !customListIdControl?.value) {
        customListValueIdControl?.disable({ emitEvent: false });
      } else {
        customListValueIdControl?.enable({ emitEvent: false });
      }

      if (contactTypeId === FieldTypes.List && this.allowEditing) {
        this.switchRequiredValidator(customListIdControl, true, false);
      } else {
        this.switchRequiredValidator(customListIdControl, false, false);
      }
    });

    customListIdControl?.valueChanges.pipe(takeUntil(this.destroyed$$)).subscribe(listId => {
      if (listId) {
        // TODO: Кэшировать данные справочников
        this.getListValues(listId)
          ?.pipe(takeUntil(this.destroyed$$))
          .subscribe(values => {
            createdContactControl.controls.contactListValues?.patchValue(values);
          });

        if (customListIdControl?.dirty) {
          createdContactControl.patchValue({ customListValueId: null, contact: null }, { emitEvent: false });
        }

        customListValueIdControl?.enable({ emitEvent: false });
      } else {
        customListValueIdControl?.disable({ emitEvent: false });
      }
    });

    customListValueIdControl?.valueChanges.pipe(takeUntil(this.destroyed$$)).subscribe(customListValueId => {
      const contactListValues: ContactsCustomListItem[] | undefined =
        createdContactControl.controls.contactListValues?.value;
      const selectedValue = contactListValues?.find(value => value.id === customListValueId);

      if (customListValueId && selectedValue) {
        createdContactControl.controls.contact?.patchValue(selectedValue.title);
      }
    });

    if (this.disabled) {
      createdContactControl.disable();
    }

    this.contacts.push(createdContactControl);
  }

  private switchRequiredValidator(control: AbstractControl, required: boolean, isValueControl = true) {
    if (isValueControl && !this.showFieldValue) return;

    if (required) {
      control?.addValidators(Validators.required);
    } else if (control?.hasValidator(Validators.required)) {
      control?.removeValidators(Validators.required);
      control?.markAsPending();
    }

    control?.updateValueAndValidity({ emitEvent: false });
  }

  private switchFileValidatorByForm(contact: FormGroup, required: boolean) {
    if (!this.showFieldValue) return;

    if (required) {
      contact?.addValidators(this.fileValidatorByForm);
    } else if (contact?.hasValidator(this.fileValidatorByForm)) {
      contact?.removeValidators(this.fileValidatorByForm);
      contact?.markAsPending();
    }

    contact?.updateValueAndValidity({ emitEvent: false });
  }

  getListValues(directoryId: Nullable<string>) {
    if (!directoryId) return;

    return this.customListService.getCustomListValuesObservable(directoryId);
  }

  @tuiPure
  contactTypesStringify(items: ReadonlyArray<FieldType>): TuiStringHandler<TuiContext<number>> {
    const map = new Map(items.map(({ id, description }) => [id, description] as [number, string]));

    return ({ $implicit }: TuiContext<number>) => this.translateService.instant(map.get($implicit) || ' ');
  }

  @tuiPure
  contactCustomListStringify(items: ReadonlyArray<ContactsCustomListItem>): TuiStringHandler<TuiContext<string>> {
    const map = new Map(items.map(({ id, title }) => [id, title] as [string, string]));

    return ({ $implicit }: TuiContext<string>) => map.get($implicit) || ' ';
  }

  private initForm() {
    this.form = new UntypedFormGroup({
      contacts: this.formBuilder.array([]),
    });
  }
}
